import { useQuery, useMutation } from "@tanstack/react-query";
import { generateHookExports } from "api/generateExports";
import { createCRUDService } from "api/queryService";
import { API_CONFIG } from "services/apiConfig";
import axios from "../../axios";
import { formatPayloadIfNeeded } from "api/utils";
import { showToast } from "services/utils";

let subQueryData =
  API_CONFIG.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTOR.parts?.find(
    (item) => item?.queryKey === "distributor"
  );
let baseData = `${API_CONFIG.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTOR?.path}/${subQueryData?.url}`;

const BASE_URL = baseData;
const QUERY_KEY = subQueryData?.queryKey;

const distributorService = createCRUDService({
  baseUrl: BASE_URL,
  queryKey: QUERY_KEY,
  custom: {
    getByLink: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, params],
        queryFn: () =>
          axios
            .get(`${BASE_URL}/get-by-link`, { params })
            .then((res) => res.data),
        ...options,
        retry: options.retry ?? 1,
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    uploadDocument: () => {
      return useMutation({
        mutationFn: ({ id, data: payload }) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false
          );
          return axios
            .put(`${BASE_URL}/upload-document/${id}`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    getUserSpecific: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, params],
        queryFn: () =>
          axios
            .get(`${BASE_URL}/get-user-specific`, { params })
            .then((res) => res.data),
        ...options,
        retry: options.retry ?? 1,
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    handleDistributor: (options = {}) =>
      useMutation({
        mutationFn: (payloadObj) =>
          axios
            .put(`${BASE_URL}/handle-distributor`, payloadObj)
            .then((res) => res.data),
        onError: (error) => {
          showToast(error?.response?.data || "Update failed");
        },
        ...options,
      }),
    fetchDistributor: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, params],
        queryFn: () =>
          axios
            .get(`${BASE_URL}/fetch-distributor`, { params })
            .then((res) => res.data),
        ...options,
        retry: options.retry ?? 1,
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    renewDistributor: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload);
          return axios
            .post(`${BASE_URL}/renew-distributor`, data, {
              headers: {},
            })
            .then((res) => res.data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
  },
});

export const {
  distributorList,
  distributorCreate,
  distributorGetById,
  distributorDownload,
  distributorUpdate,
  distributorUpdateSpecific,
  distributorDelete,
  distributorGetByLink,
  distributorGetStock,
  distributorCreateLinkingDocument,
  distributorUploadDocument,
  distributorGetUserSpecific,
  distributorHandleDistributor,
  distributorFetchDistributor,
  distributorRenewDistributor
} = generateHookExports(distributorService, "distributor");
